/**
 * Throttle function to limit the execution of a function to once every specified time interval.
 * @template {FunctionConstructor}           T     The type of the function to throttle.
 * @param   {T}                              func  The function to throttle.
 * @param   {number}                         delay Delay before each func call in milliseconds.
 * @returns {(...args: Parameters<T>): void}       The throttled function.
 */
export function throttle<T extends FunctionConstructor>(
	func: T,
	delay: number,
): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	let lastArgs: Parameters<T> | null = null;

	const invokeFunction = () => {
		if (lastArgs) {
			func(...lastArgs);
			lastArgs = null;
		}
		timeoutId = null;
	};

	return (...args: Parameters<T>) => {
		lastArgs = args;
		if (!timeoutId) {
			timeoutId = setTimeout(invokeFunction, delay);
		}
	};
}
